import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { clearStateDetails } from "./stateSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import {
  createState,
  updateState,
  getAllStates,
  getStateByID,
  getAllActiveStates,
} from "./services/state.services";
import { getAllActiveCountries } from "../country/services/country.services";
import { ICountry } from "../country/countryModel";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import { STATUSARRAY } from "../../_contstants/common";

const CreateState: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const CountryList = useAppSelector((state) => state.country.CountryList);
  const StateDetail = useAppSelector((state) => state.state.StateDetail);
  const state_guid = useAppSelector((state) => state.muidrawer.data);
  const formLoading = useAppSelector((state) => state.state.formLoading);
  const isIndirect = useAppSelector((state) => state.muidrawer.isIndirect);

  useEffect(() => {
    dispatch(getAllActiveCountries());

    return () => {
      dispatch(clearStateDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (state_guid) {
      dispatch(getStateByID(state_guid));
    }
  }, [state_guid]);

  const handleSubmit = async (values: any) => {
    try {
      if (!state_guid) {
        const insertPayload = {
          state_name: values.state_name,
          country_id: values.country_id,
          gst_state_code: values?.gst_state_code
            ? +values?.gst_state_code
            : null,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(createState(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearStateDetails());
          isIndirect
            ? dispatch(getAllActiveStates())
            : dispatch(getAllStates());
        }
      } else {
        const editPayload = {
          state_guid: state_guid,
          state_name: values.state_name,
          country_id: values.country_id,
          gst_state_code: values?.gst_state_code
            ? +values?.gst_state_code
            : null,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(updateState(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearStateDetails());
          isIndirect
            ? dispatch(getAllActiveStates())
            : dispatch(getAllStates());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={StateDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "17px 0 8px",
              }}
            >
              <Typography.h4 className="m-0">
                {state_guid ? "Update State" : "Add State"}
              </Typography.h4>
              <Button
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
              >
                <RxCross1 />
              </Button>
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="state_name"
                label="State Name"
                placeholder="Gujarat"
                component={FormTextField}
                validator={requiredValidator} astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"country_id"}
                name={"country_id"}
                label={"Country Name"}
                placeholder="India"
                component={FormSelectionField}
                validator={requiredValidator} astrike={true}
                options={CountryList?.map((country: ICountry) => {
                  return {
                    value: country?.id,
                    label: country?.country_name,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="gst_state_code"
                label="State Code"
                placeholder="00"
                type="number"
                min={0}
                maxNumber={99}
                component={FormTextField}
                validator={requiredValidator} astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"isactive"}
                name={"isactive"}
                label={"Status"}
                component={FormSelectionField}
                validator={requiredValidator} astrike={true}
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "start",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={state_guid ? "Update" : "Save"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateState;
